/**
 * PaymentSection.js
 * Orquestrador da seção de pagamento + NFS-e no AppointmentDetailPanel.
 *
 * Estados cobertos:
 *   1. Sem pagamento  → card pendente + botão "Faturar atendimento" → abre ConfirmDialog com InvoiceForm
 *      (cancelado ou falta → card "Sem cobrança", sem botão; volta ao pendente se o status mudar)
 *   2. Pago, sem NFS-e → card pago + botão "Emitir nota fiscal" (abre popup existente)
 *   3. Pago + NFS-e ativa → card pago + NfseActiveSection (acordeão)
 *   4. Histórico NFS-e → NfseHistory colapsável
 *
 * Formulários de criar/editar pagamento abrem em ConfirmDialog (fora do drawer).
 *
 * Convênio (isInsurance): o valor é cobrado da operadora, não da pessoa. Sem
 * pagamento não há "Pendente" nem "Faturar"; fica o aviso e o botão para
 * registrar só o que a pessoa pagou (coparticipação). Decisão D3 em
 * docs/features/convenios/fase-0-desenho-tecnico.md.
 */

import React, { useState, useEffect, useRef } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { formatPrice } from '../../utils/appointmentUtils';
import { NFSE_STATUS, fmtDate } from './paymentHelpers';
import InvoiceForm from './InvoiceForm';
import NfseActiveSection from './NfseActiveSection';
import NfseHistory from './NfseHistory';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import DemoTag from '../../../../../components/demo-data/DemoTag';
import { isDemoRecord } from '../../../../../components/demo-data/demoData';
import { Canceled, CanceledByPatient, Missed } from '../../../../../consts/AppointmentsState';
import {
  CheckIcon, FileIcon, EditIcon, WalletIcon, PlusIcon,
  ChevRightIcon, ChevDownIcon, ChevUpIcon,
} from './PaymentSectionIcons';
import styles from './AppointmentDetailPanel.module.css';

// Status sem cobrança: não oferece "Faturar atendimento" (um pagamento já registrado continua visível)
const NO_CHARGE_STATUSES = [Canceled, CanceledByPatient, Missed];

// company_invoices.createdAt é uma data sem hora ("2026-10-13"). new Date() leria
// como meia-noite UTC e mostraria o dia anterior no Brasil; por isso lemos o texto.
function fmtPaidDay(str) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(str || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

const INSURANCE_NOTE = 'Atendimento por convênio: o valor é cobrado da operadora, não da pessoa.';

export default function PaymentSection({ appointment, isInsurance = false, onCreateInvoice, onInvoiceCreated }) {
  const {
    GetInvoice,
    CompanyConfig,
    GetPatientById,
    GetFiscalInvoiceHistory,
  } = DataProviderInstance();

  const hasInvoice = !!(
    appointment?.invoiceId &&
    appointment.invoiceId !== '0' &&
    appointment.invoiceId !== 0
  );

  // ── Estado de dados ──────────────────────────────────────────────────────────
  const [invoiceData,    setInvoiceData]    = useState(null);
  const [sessionsUsed,   setSessionsUsed]   = useState([]); // sessões de pacote descontadas neste pagamento
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [nfseInvoices,   setNfseInvoices]   = useState([]);

  // ── Estado de UI ─────────────────────────────────────────────────────────────
  const [invoiceDialogMode, setInvoiceDialogMode] = useState(null); // null | 'create' | 'edit'
  const [showNfseDetails,   setShowNfseDetails]   = useState(false);

  // ── Paciente ─────────────────────────────────────────────────────────────────
  const patientData = appointment?.patient ? GetPatientById(appointment.patient) : null;

  // ── Loaders ──────────────────────────────────────────────────────────────────
  const loadInvoice = () => {
    if (!appointment?.invoiceId || appointment.invoiceId === '0' || appointment.invoiceId === 0) return;
    setLoadingInvoice(true);
    GetInvoice(appointment.invoiceId, (res) => {
      setLoadingInvoice(false);
      // Sem pagamento o endpoint responde 200 com invoice nulo (nunca 404)
      setInvoiceData(res?.invoice || null);
      setSessionsUsed(Array.isArray(res?.sessions_used) ? res.sessions_used : []);
    });
  };

  const loadNfse = () => {
    if (!appointment?.id) return;
    GetFiscalInvoiceHistory(appointment.id, (res) => {
      if (res?.success && Array.isArray(res.invoices)) setNfseInvoices(res.invoices);
      else setNfseInvoices([]);
    });
  };

  useEffect(() => {
    if (hasInvoice) {
      loadInvoice();
      loadNfse();
    } else {
      setInvoiceData(null);
      setSessionsUsed([]);
      setNfseInvoices([]);
    }
    setInvoiceDialogMode(null);
  }, [appointment?.invoiceId, appointment?.id]); // eslint-disable-line

  // ── Polling: NFS-e emitida mas PDF ainda não disponível ──────────────────────
  const activeInvoice = nfseInvoices.find(inv =>
    ['queued', 'processing', 'issued'].includes(inv.status)
  ) ?? null;

  const pollingRef = useRef(null);
  useEffect(() => {
    const shouldPoll = activeInvoice?.status === 'issued' && !activeInvoice?.has_pdf;
    if (shouldPoll) {
      pollingRef.current = setInterval(loadNfse, 3000);
    } else {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [activeInvoice?.status, activeInvoice?.has_pdf]); // eslint-disable-line

  // ── Dados derivados ──────────────────────────────────────────────────────────
  // 'credits' = pagamento só com sessões de pacote (valor 0, sem forma de pagamento)
  const onlyPackage = invoiceData?.paymentMethod === 'credits';
  const paymentMethodLabel = invoiceData?.paymentMethod && !onlyPackage
    ? (CompanyConfig?.PaymentOptions || []).find(
        o => String(o.Id) === String(invoiceData.paymentMethod)
      )?.Label || null
    : null;

  const priceLabel = onlyPackage
    ? null
    : (invoiceData?.price
      ? formatPrice(invoiceData.price)
      : (appointment?.price ? formatPrice(appointment.price) : null));

  const sessionsCount = sessionsUsed.length;
  const sessionsLabel = sessionsCount
    ? `${sessionsCount} ${sessionsCount === 1 ? 'sessão' : 'sessões'}`
    : null;
  const sessionsProcedures = sessionsUsed
    .map(s => (CompanyConfig?.Procedures || []).find(p => String(p.Id) === String(s.procedure_id))?.Label)
    .filter(Boolean)
    .join(', ');

  const paidAt = fmtPaidDay(invoiceData?.createdAt);
  const paidBy = invoiceData?.createdByName || null;

  const nfseStatus = activeInvoice?.status || 'pending';
  const nfseCfg    = NFSE_STATUS[nfseStatus] || NFSE_STATUS.pending;

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleInvoiceSuccess = () => {
    setInvoiceDialogMode(null);
    loadInvoice();
    loadNfse();
    // Notifica o pai para recarregar o calendário e atualizar o drawerAppointment
    onInvoiceCreated?.();
  };

  const handleEmitirNota = () => {
    if (typeof onCreateInvoice === 'function') {
      onCreateInvoice(appointment);
    }
  };

  // ── Dialog de faturamento (criar / editar) ────────────────────────────────────
  const invoiceDialogTitle = invoiceDialogMode === 'create'
    ? 'Registrar pagamento'
    : 'Editar pagamento';

  // ── Render: SEM pagamento ────────────────────────────────────────────────────
  if (!hasInvoice && NO_CHARGE_STATUSES.includes(appointment?.status)) {
    return (
      <div className={styles.paymentSection}>
        <div className={styles.paymentHeader}>
          <span className={styles.paymentSectionLabel}>PAGAMENTO</span>
          <span className={styles.paymentBadgeNeutral}>Sem cobrança</span>
        </div>
        <div className={styles.paymentCardNeutral}>
          <div className={styles.paymentNeutralIconWrapper}><WalletIcon /></div>
          <div className={styles.paymentPendingInfo}>
            <div className={styles.paymentPendingTitle}>Sem cobrança</div>
            <div className={styles.paymentPendingMeta}>Cancelado ou com falta: nada a faturar</div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasInvoice && isInsurance) {
    return (
      <div className={styles.paymentSection}>
        <div className={styles.paymentHeader}>
          <span className={styles.paymentSectionLabel}>PAGAMENTO</span>
          <span className={styles.paymentBadgeInsurance}>Convênio</span>
        </div>

        <div className={styles.paymentCardInsurance}>
          <div className={styles.paymentInsuranceIconWrapper}><WalletIcon /></div>
          <div className={styles.paymentPendingInfo}>
            <div className={styles.paymentPendingTitle}>Cobrança pela operadora</div>
            <div className={styles.paymentInsuranceText}>
              {INSURANCE_NOTE} Registre aqui só o que a pessoa pagou (coparticipação).
            </div>
            <button type="button" className={styles.paymentInsuranceBtn}
              onClick={() => setInvoiceDialogMode('create')}>
              <PlusIcon />
              <span>Registrar pagamento da pessoa</span>
            </button>
          </div>
        </div>

        <ConfirmDialog
          isOpen={invoiceDialogMode === 'create'}
          title="Registrar pagamento da pessoa"
          variant="info"
          onCancel={() => setInvoiceDialogMode(null)}
          hideFooter
        >
          <InvoiceForm
            appointment={appointment}
            invoiceData={null}
            mode="create"
            copay
            onSuccess={handleInvoiceSuccess}
            onCancel={() => setInvoiceDialogMode(null)}
          />
        </ConfirmDialog>
      </div>
    );
  }

  if (!hasInvoice) {
    const appointmentPrice = appointment?.price ? formatPrice(appointment.price) : null;
    return (
      <div className={styles.paymentSection}>
        <div className={styles.paymentHeader}>
          <span className={styles.paymentSectionLabel}>PAGAMENTO</span>
          <span className={styles.paymentBadgePending}>Pendente</span>
        </div>

        <div className={styles.paymentCardPending}>
          <div className={styles.paymentPendingIconWrapper}><WalletIcon /></div>
          <div className={styles.paymentPendingInfo}>
            <div className={styles.paymentPendingTitle}>Consulta não faturada</div>
            <div className={styles.paymentPendingMeta}>Valor da consulta</div>
            {appointmentPrice && (
              <div className={styles.paymentPendingPrice}>{appointmentPrice}</div>
            )}
          </div>
          <button type="button" className={styles.paymentFaturarBtn} data-tour="payment-invoice"
            onClick={() => setInvoiceDialogMode('create')}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
            <span>Faturar atendimento</span>
          </button>
        </div>

        {/* ── Dialog: criar faturamento ── */}
        <ConfirmDialog
          isOpen={invoiceDialogMode === 'create'}
          title={invoiceDialogTitle}
          variant="info"
          onCancel={() => setInvoiceDialogMode(null)}
          hideFooter
        >
          <InvoiceForm
            appointment={appointment}
            invoiceData={null}
            mode="create"
            onSuccess={handleInvoiceSuccess}
            onCancel={() => setInvoiceDialogMode(null)}
          />
        </ConfirmDialog>
      </div>
    );
  }

  // ── Render: COM pagamento ────────────────────────────────────────────────────
  return (
    <div className={styles.paymentSection}>
      <div className={styles.paymentHeader}>
        <span className={styles.paymentSectionLabel}>PAGAMENTO</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {isDemoRecord(invoiceData) && <DemoTag />}
          <span className={styles.paymentBadgePaid}>Pago</span>
        </span>
      </div>

      {isInsurance && <div className={styles.paymentInsuranceNote}>{INSURANCE_NOTE}</div>}

      {loadingInvoice ? (
        <div className={styles.paymentLoadingRow}>
          <span className={styles.spinner} />
          <span>Carregando...</span>
        </div>
      ) : (
        <div className={styles.paymentCard}>

          {/* ── Linha 1: check + método + valor ── */}
          <div className={styles.paymentMainRow}>
            <div className={styles.paymentCheckIcon}><CheckIcon /></div>
            <div className={styles.paymentMainInfo}>
              <div className={styles.paymentMethodLabel}>
                {onlyPackage
                  ? 'Pago com sessões de pacote'
                  : (paymentMethodLabel ? `Pago via ${paymentMethodLabel}` : 'Pagamento registrado')}
              </div>
              <div className={styles.paymentMeta}>
                {paidAt && <span>{paidAt}</span>}
                {paidBy && <span> · Por: {paidBy}</span>}
              </div>
              {sessionsLabel && (
                <div className={styles.paymentSessions} title={sessionsProcedures || undefined}>
                  {sessionsLabel} de pacote{sessionsProcedures ? `: ${sessionsProcedures}` : ''}
                </div>
              )}
            </div>
            {priceLabel && (
              <div className={styles.paymentValueBlock}>
                <div className={styles.paymentValue}>{priceLabel}</div>
                <div className={styles.paymentValueLabel}>Valor recebido</div>
              </div>
            )}
            {!priceLabel && sessionsLabel && (
              <div className={styles.paymentValueBlock}>
                <div className={styles.paymentValue}>{sessionsLabel}</div>
                <div className={styles.paymentValueLabel}>Descontadas do pacote</div>
              </div>
            )}
          </div>

          {/* ── Botão editar pagamento ── */}
          <div className={styles.paymentActions}>
            <button type="button" className={styles.paymentActionBtn}
              onClick={() => setInvoiceDialogMode('edit')}>
              <EditIcon /><span>Editar pagamento</span>
            </button>
          </div>

          {/* ── Linha 2: NFS-e — acordeão (ativa) ou botão emitir ── */}
          <div
            className={activeInvoice ? styles.paymentNfRow : styles.paymentNfRowPending}
            onClick={activeInvoice
              ? () => setShowNfseDetails(p => !p)
              : handleEmitirNota}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              if (activeInvoice) setShowNfseDetails(p => !p);
              else handleEmitirNota();
            }}
          >
            <span className={activeInvoice ? styles.paymentNfIconIssued : styles.paymentNfIconPending}>
              <FileIcon />
            </span>
            <div className={styles.paymentNfInfo}>
              <span className={activeInvoice ? styles.paymentNfLabelIssued : styles.paymentNfLabelPending}>
                {activeInvoice
                  ? `Nota fiscal ${nfseCfg.label.toLowerCase()}`
                  : 'Nota fiscal não emitida'}
              </span>
              {activeInvoice?.issued_at && (
                <div className={styles.paymentNfSub}>
                  <span>{fmtDate(activeInvoice.issued_at)}</span>
                </div>
              )}
            </div>
            {!activeInvoice && (
              <button type="button" className={styles.paymentNfEmitBtn}
                onClick={e => { e.stopPropagation(); handleEmitirNota(); }}>
                Emitir nota fiscal
              </button>
            )}
            {activeInvoice && (
              <span className={styles.paymentNfChevron}>
                {showNfseDetails ? <ChevUpIcon /> : <ChevDownIcon />}
              </span>
            )}
          </div>

          {/* ── Detalhes NFS-e ativa — expansível via acordeão ── */}
          {activeInvoice && showNfseDetails && (
            <NfseActiveSection
              activeInvoice={activeInvoice}
              appointment={appointment}
              patientData={patientData}
              onRefresh={loadNfse}
            />
          )}

          {/* ── Histórico NFS-e ── */}
          {nfseInvoices.length > 0 && (
            <NfseHistory invoices={nfseInvoices} />
          )}

        </div>
      )}

      {/* ── Dialog: editar faturamento ── */}
      <ConfirmDialog
        isOpen={invoiceDialogMode === 'edit'}
        title={invoiceDialogTitle}
        variant="info"
        onCancel={() => setInvoiceDialogMode(null)}
        hideFooter
      >
        <InvoiceForm
          appointment={appointment}
          invoiceData={invoiceData}
          sessionsUsed={sessionsUsed}
          mode="edit"
          onSuccess={handleInvoiceSuccess}
          onCancel={() => setInvoiceDialogMode(null)}
        />
      </ConfirmDialog>
    </div>
  );
}
